import React, { useEffect, useRef, useState } from 'react';
import { motion, useReducedMotion, type PanInfo } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { CtaButton } from './CtaButton';
import { PageLightbox } from './PageLightbox';
import { lookInsidePages } from '../data/lookInsidePages';
const ease = [0.23, 1, 0.32, 1] as const;
export function LookInside() {
  const viewportRef = useRef<HTMLDivElement>(null);
  const draggedRef = useRef(false);
  const [width, setWidth] = useState(0);
  const [isDesktop, setIsDesktop] = useState(false);
  const [active, setActive] = useState(0);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const reduceMotion = useReducedMotion();
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const mq = window.matchMedia('(min-width: 768px)');
    const update = () => {
      setWidth(el.clientWidth);
      setIsDesktop(mq.matches);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    mq.addEventListener('change', update);
    return () => {
      ro.disconnect();
      mq.removeEventListener('change', update);
    };
  }, []);
  const count = lookInsidePages.length;
  const gap = isDesktop ? 32 : 16;
  const slideWidth = isDesktop ? (width - gap * 2) / 3 : width * 0.8;
  const step = slideWidth + gap;
  const x = width / 2 - (active * step + slideWidth / 2);
  const goTo = (i: number) => setActive(Math.max(0, Math.min(count - 1, i)));
  const onDragEnd = (_: unknown, info: PanInfo) => {
    const swipe = info.offset.x + info.velocity.x * 0.2;
    if (Math.abs(swipe) > slideWidth * 0.2) {
      const jump = Math.max(1, Math.round(Math.abs(swipe) / step));
      goTo(active + (swipe < 0 ? jump : -jump));
    }
    window.setTimeout(() => {
      draggedRef.current = false;
    }, 0);
  };
  const openSlide = (i: number) => {
    if (draggedRef.current) return;
    setActive(i);
    setLightboxIndex(i);
  };
  const arrowBtn = 'absolute top-[calc(50%-1.25rem)] z-10 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-navy text-white shadow-card transition-[background-color,opacity] duration-150 hover:bg-teal-deep focus:outline-none focus-visible:ring-4 focus-visible:ring-teal/40 disabled:pointer-events-none disabled:opacity-30 md:flex';
  return <section aria-labelledby="look-inside-heading" className="paper-grain w-full overflow-hidden bg-paper text-navy" data-id="element-175" data-mp="1heyngdd3xwsi">
      <div className="mx-auto max-w-5xl px-5 py-16 sm:py-20" data-id="element-176" data-mp="krtxe4weu90p">
        <div className="text-center" data-id="element-177" data-mp="pq3u4jxs9hof">
          <h2 id="look-inside-heading" className="font-display text-3xl font-semibold text-navy sm:text-5xl" data-id="element-178" data-mp="pr6juojdde2r">
            Take a look inside
          </h2>
          <p className="mx-auto mt-4 max-w-[60ch] text-base text-navy-soft" data-id="element-179" data-mp="yq0mownan0or">
            Real pages from Volume 4 · Connect.
          </p>
        </div>

        <div className="relative mt-10" role="region" aria-roledescription="carousel" aria-label="Pages from Volume 4" data-id="element-180" data-mp="2586zxvephggi">
          <div ref={viewportRef} className="-my-6 py-6 md:overflow-hidden" data-id="element-181" data-mp="2k8civ23eywld">
            <motion.ul className="flex cursor-grab touch-pan-y items-start active:cursor-grabbing" style={{
            gap
          }} drag={isDesktop ? false : 'x'} dragConstraints={{
            left: x - step,
            right: x + step
          }} dragElastic={0.15} dragMomentum={false} onDragStart={() => {
            draggedRef.current = true;
          }} onDragEnd={onDragEnd} animate={{
            x: width ? x : 0
          }} transition={reduceMotion ? {
            duration: 0
          } : {
            duration: 0.28,
            ease
          }} data-id="element-182" data-mp="2fassj3tfytd2">
              {lookInsidePages.map((page, i) => {
              const isActive = i === active;
              return <li key={page.src} className="shrink-0" style={{
                width: slideWidth || '80%'
              }} aria-roledescription="slide" aria-label={`${i + 1} of ${count}`} aria-hidden={isDesktop ? Math.abs(i - active) > 1 : !isActive} data-id="element-183" data-mp="3lxocbbqvks3w" data-mpi={i}>
                    <figure data-id="element-184" data-mp="3fzwb3u8rgjl1">
                      <motion.button type="button" onClick={() => openSlide(i)} aria-label={`Open larger: ${page.alt}`} tabIndex={isActive ? 0 : -1} className="block w-full overflow-hidden rounded-xl bg-white shadow-card focus:outline-none focus-visible:ring-4 focus-visible:ring-teal/40" animate={{
                    scale: isDesktop && !isActive ? 0.88 : 1,
                    opacity: isActive ? 1 : 0.7
                  }} transition={reduceMotion ? {
                    duration: 0
                  } : {
                    duration: 0.25,
                    ease
                  }} data-id="element-185" data-mp="3fyl6js7hr7sv">
                        <img src={page.src} alt={page.alt} loading="lazy" draggable={false} width={1080} height={1920} className="aspect-[9/16] w-full select-none object-contain" data-id="element-186" data-mp="2s8qydny4a7ey" />
                      </motion.button>
                      <figcaption className={`mt-4 text-center text-sm transition-colors duration-200 ${isActive ? 'font-semibold text-navy' : 'text-navy-soft'}`} data-id="element-187" data-mp="2rmmhoi9s5q22">
                        {page.caption}
                      </figcaption>
                    </figure>
                  </li>;
            })}
            </motion.ul>
          </div>

          <button type="button" onClick={() => goTo(active - 1)} disabled={active === 0} aria-label="Previous page" className={`${arrowBtn} left-0 lg:-left-4`} data-id="element-188" data-mp="3adnd4wboz3gv">
            <ChevronLeftIcon className="h-6 w-6" aria-hidden="true" data-id="element-189" data-mp="22ys2clcn5orj" />
          </button>
          <button type="button" onClick={() => goTo(active + 1)} disabled={active === count - 1} aria-label="Next page" className={`${arrowBtn} right-0 lg:-right-4`} data-id="element-190" data-mp="27xlhsjbhnpuc">
            <ChevronRightIcon className="h-6 w-6" aria-hidden="true" data-id="element-191" data-mp="2lrm8qqw788k5" />
          </button>
        </div>

        <div className="mt-6 flex justify-center gap-2" role="tablist" aria-label="Choose page" data-id="element-192" data-mp="2qpny1tk7x442">
          {lookInsidePages.map((page, i) => <button key={page.src} type="button" role="tab" aria-selected={active === i} aria-label={`Go to page ${i + 1}: ${page.caption}`} onClick={() => goTo(i)} className="flex h-6 w-6 items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-teal" data-id="element-193" data-mp="2vq1wqkzxj1pf" data-mpi={i}>
              <span className={`block h-2 rounded-full transition-[width,background-color] duration-200 ease-out ${active === i ? 'w-6 bg-navy' : 'w-2 bg-paper-dark'}`} data-id="element-194" data-mp="xvepaxaivb9m" />
            </button>)}
        </div>

        <div className="mt-10 flex justify-center" data-id="element-195" data-mp="k3hwss4xyzcp">
          <div className="w-full max-w-md" data-id="element-196" data-mp="1gspn0dgi9wmy">
            <CtaButton href="#pricing" data-id="element-197" data-mp="1gro11dxybpqg">Get 52 Weeks of Art-Based Sessions</CtaButton>
          </div>
        </div>
      </div>

      <PageLightbox pages={lookInsidePages} index={lightboxIndex} onClose={() => setLightboxIndex(null)} onChange={i => {
      setLightboxIndex(i);
      setActive(i);
    }} data-id="element-198" data-mp="1bra1ylebvq4k" />
    </section>;
}